import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Chip, CircularProgress, Alert, TablePagination, Breadcrumbs, Button,
    TextField, InputAdornment, FormControl, Select, MenuItem
} from '@mui/material';
import { Refresh, Fastfood, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllFoodBookings, getActiveServiceLocations } from '../../../services/Api';

const GetAllFoodBookings = () => {
    const navigate = useNavigate();
    
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [locations, setLocations] = useState([]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 1000);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const res = await getActiveServiceLocations();
                if (res.success) {
                    setLocations(res.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch locations', err);
            }
        };
        fetchLocations();
    }, []);

    const fetchBookings = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllFoodBookings(page + 1, rowsPerPage, debouncedSearch, statusFilter, paymentStatusFilter, locationFilter);
            if (response.success || response.data) {
                const src = response.data || response;
                let targetArray = [];

                if (Array.isArray(src)) targetArray = src;
                else if (src && typeof src === 'object') {
                    if (Array.isArray(src.bookings)) targetArray = src.bookings;
                    else if (Array.isArray(src.data)) targetArray = src.data;
                }

                setBookings(targetArray);
                setTotalRows(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || 0);
            } else {
                setError(response.message || 'Failed to fetch bookings');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching bookings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, debouncedSearch, statusFilter, paymentStatusFilter, locationFilter]);



    const handleChangePage = (event, newPage) => setPage(newPage);
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status) => {
        const s = status?.toLowerCase();
        if (s === 'completed' || s === 'delivered') return { bgcolor: 'success.light', color: 'success.dark' };
        if (s === 'cancelled' || s === 'rejected') return { bgcolor: 'error.light', color: 'error.dark' };
        if (s === 'pending' || s === 'processing') return { bgcolor: 'warning.light', color: 'warning.dark' };
        return { bgcolor: 'grey.200', color: 'grey.800' };
    };

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Typography color="text.primary">Bookings</Typography>
                    <Typography color="text.primary">Food Bookings</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Fastfood sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center' }}>
                                Food Bookings
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage all food delivery orders efficiently
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={fetchBookings} 
                        disabled={loading} 
                        sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider' }}
                    >
                        <Refresh />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                            displayEmpty
                            sx={{ borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.02)' }}
                        >
                            <MenuItem value="" disabled>Booking Status</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="confirmed">Confirmed</MenuItem>
                            <MenuItem value="processing">Processing</MenuItem>
                            <MenuItem value="out_for_delivery">Out for Delivery</MenuItem>
                            <MenuItem value="delivered">Delivered</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={paymentStatusFilter}
                            onChange={(e) => { setPaymentStatusFilter(e.target.value); setPage(0); }}
                            displayEmpty
                            sx={{ borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.02)' }}
                        >
                            <MenuItem value="" disabled>Payment Status</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={locationFilter}
                            onChange={(e) => { setLocationFilter(e.target.value); setPage(0); }}
                            displayEmpty
                            sx={{ borderRadius: '20px', bgcolor: 'rgba(0,0,0,0.02)' }}
                        >
                            <MenuItem value="">All Locations</MenuItem>
                            {locations.map((loc) => (
                                <MenuItem key={loc.id} value={loc.id}>
                                    {loc.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        placeholder="Search..."
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            width: 180,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                backgroundColor: 'rgba(0,0,0,0.02)',
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Booking </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Vendor</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : bookings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No food bookings found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                bookings.map((booking, index) => (
                                    <TableRow key={booking.id || booking._id} hover>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{booking.bookingNumber || `#${booking.id}`}</TableCell>
                                        <TableCell>{booking.user?.name || '-'}</TableCell>
                                        <TableCell>{booking.foodVendor?.shopName || '-'}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>₹{booking.grandTotal || '0'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.status || 'Pending'}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize',
                                                    ...getStatusColor(booking.status)
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell align="right">
                                            <Button 
                                                size="small" 
                                                variant="outlined" 
                                                onClick={() => navigate(`/bookings/food/details/${booking.id}`)}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                View Details
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default GetAllFoodBookings;
